import {
  AskConfirmation,
  buttonsContinueCancel,
  buttonsSaveCancel
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { asMarkdownTemplate } from '../../../../webmodule-common/other/general/markdown';
import {
  assetContentTag,
  fetchQuoteAsset,
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  getQuoteFrameCustomerVersions,
  isV6
} from '../../../quotes/data/v6/helper-functions';
import { batchHeaderDate, renderBatchHeaderDetail } from '../../../quotes/ui/batch-header-detail';
import { BranchQuoteSupportStatus, type TableColumnConfiguration } from '../../../api/dealer-api-interface-franchisee';
import { buildQuoteItemRows, type QuoteItemGroupHeader } from './quote-item-grouping-rows';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { consume } from '@lit/context';
import { css, html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { type DeliveryCandidate, RecordDeliveryDialog } from '../../../fabrication-jobs/views/record-delivery-dialog';
import {
  EventTemplate,
  type FrameBuyInItem,
  FrameValidationType,
  type QCQuoteValues,
  type QCValue,
  Snippet
} from '../../../../webmodule-common/interop/webmodule-interop';
import { fabricationJobPicker } from './fabrication-job-picker';
import {
  findGroup,
  getGroups,
  normalizeBatchDate,
  primaryBatchAttributeKey,
  type QuoteItemGroupingDocument
} from '../../../quotes/data/quote-item-grouping';
import { fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away';
import { firstValidString, isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { frameExpanderStyles } from '../../../frames/frame-expander-styles';
import { frameValidationIcon, getBuyInDetailsTemplate, getFrameDetailsTemplate } from '../../../frames/ui';
import { getApiFactory } from '../../../api/api-injector';
import {
  getFabricationJobStatus,
  getQuoteItemFabricationStatusIcon
} from '../../../fabrication-jobs/data/fabrication-job-helper-functions';
import {
  getQuoteTitleStr,
  isAnyFreehand,
  isDealerQuotePriceAdjustment,
  isDefaultShipping,
  isFrame,
  isShipping,
  isSpecialItem,
  isSSI,
  isSupplierSpecialItem,
  quoteItemThumbSrc
} from '../../../quotes/data/quote-helper-functions';
import { getToolTip } from '../../../../webmodule-common/other/tooltip';
import { getV6QuoteItemSubtitleOrFrameDimensions } from '../../../v6config/v6-ui';
import { goURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { highestValidationRank, rankToValidationType } from '../../../v6config/validation-rank';
import { ImagePreview, imagePreviewContext } from '../../../../webmodule-common/other/context/imagePreview';
import { inCellBadgeLinkStyles } from '../../../../webmodule-common/other/ui/in-cell-badge-link-styles';
import { interpolateExternal } from '../../../../localization/global';
import { isCynclyStaff, isSupplierAgent } from '../../../../webmodule-common/other/currentuser-claims';
import { isDebugMode } from '../../../../webmodule-common/other/debug';
import { isSameDeliveryBatch } from '../../../fabrication-jobs/data/delivery-batch';
import {
  isValidV6ConfigVersion,
  v6SupportsVersion,
  v6VersionMap
} from '../../../../webmodule-common/interop/interfaces/v6-config';
import { LangFabricationJobActions } from '../../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import { LangFranchiseeQuoteManager } from '../../../../localization/scripts/dealer-franchisee/quotes/data/franchisee-quote-manager-lang';
import { LitElementLangBase } from '../../../../webmodule-common/other/litelement-base';
import { localDateToRFC3339, serverDateToLocalRFC3339 } from '../../../../webmodule-common/other/datetime-converter';
import { ModalDialog } from '../../../../webmodule-common/other/ui/modal-base';
import {
  moneyToTemplateResult,
  number2dpToHtml,
  number4dpToHtml
} from '../../../../webmodule-common/other/currency-formatter';
import { monitor } from '../../../../webmodule-common/other/monitor';
import { type QuoteItem, type QuoteItemPriceBreakdown, QuoteItemState } from '../../../api/dealer-api-interface-quote';
import { quoteItemAction } from '../../../quotes/data/events';
import { quoteItemCellStyles } from '../../../../webmodule-common/other/ui/expandable-item-cell-styles';
import { QuoteItemFabricationStatus } from '../../../api/fabrication-job-api';
import { QuoteItemGroupDialog } from './quote-item-group-dialog';
import { supplierItemContentTypeDisplay } from '../../../quotes/data/supplier-quote-item-content-type';
import { tryAsNumberString } from '../../../../webmodule-common/other/number-utilities';
import { userDataStore } from '../../common/current-user-data-store';
import { V6BomViewer } from './v6/franchisee-v6-bom-viewer';

import {
  type WebModuleLitTable,
  type WebModuleLitTableColumnDef,
  type WebModuleTable,
  type WebModuleTableColumnDef,
  type WebModuleTableEmptyGroup,
  type WebModuleTableItemMove,
  WebmoduleToggle
} from '../../../../webmodule-common/components/src/webmodule-components';
import { when } from 'lit/directives/when.js';
import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import type { QuoteApi, QuoteItemAssetType } from '../../../api/quote-api';
import type { QuoteItemContainer } from '../../../quotes/data/quote-item-container';

/**
 * Styles injected into the <webmodule-table>'s shadow root via its `customStyles`
 * hook. The table renders cell, group-header and row-extension content inside its
 * shadow DOM, so the host page's global quote-item CSS cannot reach it; the rules
 * the quote items table depends on are ported here. Colours reference the app's
 * inherited CSS custom properties (declared on :root/body), which do cross the
 * shadow boundary.
 */
const quoteItemsTableStyles = css`
  ${inCellBadgeLinkStyles}

  ${quoteItemCellStyles}

  /* Tighten cell padding to the legacy data-table density (~8px/12px). The
     component default (1rem) is too generous for this many financial columns:
     with border-box sizing it eats the narrow numeric columns (Multi, Tags) and
     pushes the table wider than the viewport. Scoped to this table only. */
  .wm-table {
    --wm-table-cell-padding-x: 0.75rem;
  }

  /* Price column groupings: supplier columns read as a neutral band. */
  .wm-table-header .wm-table-cell.supplier-price-col {
    background: var(--gray-800);
    color: var(--white);
  }
  .wm-table-content .wm-table-cell.supplier-price-col,
  .wm-table-footer .wm-table-cell.supplier-price-col {
    background: var(--shadow-10);
  }

  /* Deleted / cancelled-support rows: dim the thumbnail, strike the title + price. */
  .wm-table-row.quote-item-deleted .quote-item-thumbnail,
  .wm-table-row.quotesupport-status-cancelled .quote-item-thumbnail {
    opacity: 0.33;
  }
  .wm-table-row.quote-item-deleted .quote-item-title *,
  .wm-table-row.quote-item-deleted .quote-item-price *,
  .wm-table-row.quotesupport-status-cancelled .quote-item-title *,
  .wm-table-row.quotesupport-status-cancelled .quote-item-price * {
    color: var(--red);
    text-decoration: line-through;
  }

  /* Items that belong to a group: carry the group accent down the left edge and
     indent the title so grouped rows read as nested under their header. Use an
     inset box-shadow (not border-left) so the 3px accent does not shift the row's
     cells and break alignment with the group footer / ungrouped rows. */
  .wm-table-row.quote-item-grouped {
    box-shadow: inset 3px 0 0 var(--st-blue);
  }
  .wm-table-row.quote-item-grouped .quote-item-title {
    padding-left: 1.5rem;
  }

  /* Group-header content (rendered inside the table's native sticky header cell). */
  .quote-item-group-title {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    white-space: nowrap;
  }
  /* Inline batch "fast view" (delivery date / customer ref), badged so it carries the same weight as the
     item-count badge next to it rather than reading as incidental grey text. */
  .batch-header-detail {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-left: 0.5rem;
    white-space: nowrap;
  }
  .batch-header-badge {
    font-weight: 400;
    background: var(--overwrite-main);
    color: var(--overwrite-light);
  }

  .quote-item-group-badge {
    font-weight: 400;
    background: var(--overwrite-main);
    color: var(--overwrite-light);
  }
  /* Let the group header fill the row so it reads as a full-width banner. The "..."
     menu now lives in the table's pinned group-action region (sticky right edge),
     not inside this cell, so it no longer scrolls off with the wide header. */
  .wm-table-group-header-cell {
    flex: 1 1 auto;
  }

  /* Group action menu: sized to match the per-row action column (3rem) and centred
     so it sits directly under the column's menus at the pinned right edge. */
  .quote-item-group-menu {
    width: 3rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .quote-item-group-menu webmodule-icon-button {
    --webmodule-color-neutral-600: var(--overwrite-main);
  }

  /* Keep the "Tags" header label on one line (the column is wide enough for the
     label + (?) tooltip with normal padding, but stop it breaking to "Tag s"). */
  .wm-table-header .wm-table-cell.quote-item-tags .wm-table-header-label {
    white-space: nowrap;
  }

  /* Clickable quote total in the sub-footer summary row. */
  .quote-item-total-button {
    cursor: pointer;
    text-decoration: none;
    font-weight: inherit;
  }
  .quote-item-total-button:hover {
    text-decoration: none;
  }

  /* The fabrication status cell carries two badges - the item's own status and its job's -
     so they need to sit side by side and wrap rather than collide in a narrow column. */
  .fabrication-status-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
    justify-content: center;
  }

  /* Expander (row-extension) content is rendered by its own component
     (<franchisee-quote-item-expander>), which carries its own encapsulated,
     theme-aware styles in its shadow root - so none of those rules live here. */
`;

/**
 * Cache key for an item's thumbnail URL - the asset's own content hash, so the URL changes exactly
 * when the image does. See assetContentTag for why the URL (not just the ETag) has to change.
 * Falls back to the record version for items saved before hash tracking existed.
 *
 * Module scope rather than a method: both tables in this file render thumbnails and must agree on the
 * key, or the same image ends up cached twice under two URLs that can disagree.
 */
function thumbnailTag(quoteManager: FranchiseeQuoteContainerManager, item: QuoteItem): string | undefined {
  return assetContentTag(
    quoteManager.quoteItemContainer(item.id)?.data?.assetVirtualPaths,
    'thumbnail',
    item.recordVersion
  );
}

@customElement('franchisee-quote-item-table')
export class FranchiseeQuoteItemTable extends LitElementLangBase {
  quoteApi: QuoteApi = getApiFactory().quote();
  @query('#quote-items-table')
  table?: WebModuleTable;
  @property({
    type: Array,
    hasChanged(newVal: object, oldVal: object) {
      return newVal !== oldVal;
    }
  })
  public columnConfiguration: TableColumnConfiguration[] = [];
  @property({ type: Object })
  public quoteManager!: FranchiseeQuoteContainerManager;

  /** Invoked when the price-adjustment button in the menu-column header is clicked. */
  @property({ attribute: false })
  public onRunPriceAdjustment?: () => void | Promise<void>;

  /** Invoked when the quote total in the footer summary row is clicked, to open the pricing summary drawer. */
  @property({ attribute: false })
  public onOpenPricingSummary?: () => void | Promise<void>;

  @consume({ context: imagePreviewContext })
  @property({ attribute: false })
  public imagePreview?: ImagePreview;

  @property({ type: Boolean })
  private _showDeleted?: boolean | undefined;
  public get showDeleted(): boolean | undefined {
    return this._showDeleted;
  }
  public set showDeleted(value: boolean | undefined) {
    if (this._showDeleted === value) return;
    this._showDeleted = value;
    this.refreshData();
  }

  @state()
  private _columnsData: WebModuleTableColumnDef[] = [];
  @state()
  private _data: QuoteItem[] = [];
  @state()
  private _columns: WebModuleTableColumnDef[] = [];
  /** Signature of the quote-calculation (NFRC) column set last built into the table.
   * Used to detect when an SSI round-trip changes the column set so it can be replaced
   * without a manual refresh (Task 229897). Undefined until the first build. */
  private _qcColumnKey?: string;
  @state()
  private _expandedOptions = false;
  /** item commonId -> containing group id, rebuilt with the rows on every refresh */
  private _groupIdByCommonId = new Map<string, string>();
  /**
   * item commonId -> per-section display number (Task 230006), rebuilt on every refresh. Held as a field
   * rather than captured inside getColumns because getColumns is only re-run when the column *set* changes
   * (QC discovery); a grouping move keeps the same columns, so the numbers must refresh here to renumber.
   */
  private _itemNumbers = new Map<string, string>();
  /** group id -> header metadata (name, attributes, members), rebuilt on every refresh.
   * Used by the native group-header template; the table derives group placement
   * from {@link _groupIdByCommonId} via getGroupByKey. */
  private _headerById = new Map<string, QuoteItemGroupHeader>();
  /** Groups with no live member rows, fed to the table so their headers still
   * render (and stay drop targets); rebuilt with the rows on every refresh. */
  private _emptyGroups: WebModuleTableEmptyGroup[] = [];

  private _fabricationStatusUnsub?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    if (userDataStore.isFabricatorMode) {
      this._fabricationStatusUnsub = this.quoteManager.onFabricationStatusesChanged(() => {
        this.requestUpdate();
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._fabricationStatusUnsub?.();
    this._fabricationStatusUnsub = undefined;
  }

  dispatchCustom(values: object) {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };

    this.dispatchEvent(new CustomEvent(`wm-quote-item-action`, options));
  }

  @monitor('columnConfiguration', { delayUntilFirstUpdate: true })
  public updateCols() {
    const colDisplay = this._columnsData.map(x => {
      const config = this.columnConfiguration.find(y => y.code == x.id);

      return { visible: config?.defaultDisplay, ...x };
    });

    this._columns = [
      ...colDisplay,
      ...[
        {
          title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
          classes: 'item-menu',
          // Pinned to the right edge so the row actions stay visible while the
          // table scrolls horizontally.
          fixed: 'right',
          width: '3rem',
          align: 'center',
          fieldName: 'xx',
          displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
            const rowItem = item as QuoteItem;

            return this.ellipsisMenu(rowItem);
          },
          // Price-adjustment action lives in the second (quote total) summary row menu cell, not on each row.
          columnSubAggregate: () =>
            html` <webmodule-icon-button
              class="item-menu-price-adjustment"
              style="color: var(--webmodule-color-primary)"
              library="fa"
              name="fas-sliders"
              title=${LangFranchiseeQuoteItemsView.priceAdjustment()}
              @click=${() => this.onRunPriceAdjustment?.()}
            ></webmodule-icon-button>`,
          visible: true
        } as WebModuleTableColumnDef
      ]
    ];
  }

  public async refreshData() {
    await this.quoteManager.needsQuoteItems(false);
    await this.quoteManager.needsQuoteSupport();
    await this.quoteManager.needsItemFabricationSummaryData();
    await this.quoteManager.performItemSorting();
    // load (or reconcile) the grouping document so group headers can be injected
    await this.quoteManager.needsItemGrouping();

    const built = buildQuoteItemRows(this.quoteManager, this.showDeleted === true);

    // Per-section item numbers (Task 230006): recomputed every refresh so a move/regroup renumbers
    // immediately (getColumns, where the cell reads this, is not re-run on a move).
    this._itemNumbers = this.quoteManager.itemDisplayNumbers();

    // Fabrication statuses are owned by the quote manager (loaded via
    // needsItemFabricationSummaryData / refreshItemFabricationStatuses) and read
    // during row rendering from quoteManager.itemFabricationStatuses.
    this._data = built.rows;
    this._groupIdByCommonId = built.groupIdByCommonId;
    // The new table renders group headers natively (from getGroupByKey); keep a
    // lookup of header metadata so the header template can show name/badges/menu.
    const headers = [...built.headersBeforeIndex.values()].flat();
    headers.push(...built.trailingHeaders);
    this._headerById = new Map(headers.map(header => [header.id, header]));
    // Empty groups have no member rows, so native grouping renders no header for
    // them. Feed them to the table separately, anchored at the row index their
    // header attaches above (the map key), or after the last row for trailing
    // groups, so they stay visible and remain drop targets.
    const emptyGroups: WebModuleTableEmptyGroup[] = [];
    for (const [index, groupHeaders] of built.headersBeforeIndex) {
      for (const header of groupHeaders) {
        if (header.itemCommonIds.length === 0)
          emptyGroups.push({ key: header.id, value: header.id, beforeIndex: index });
      }
    }
    for (const header of built.trailingHeaders) {
      if (header.itemCommonIds.length === 0)
        emptyGroups.push({ key: header.id, value: header.id, beforeIndex: built.rows.length });
    }
    this._emptyGroups = emptyGroups;

    // An SSI round-trip can change the set of quote-calculation (NFRC) columns — e.g. the
    // first frame added to a new quote surfaces columns that did not exist when the table
    // first built its columns. Replace the column set when that happens so the new columns
    // appear without a manual refresh (Task 229897). Column-building logic is unchanged;
    // getColumns() is simply re-run when discovery differs. Skipped on the initial refresh
    // (before firstUpdated seeds _qcColumnKey), which builds the columns unconditionally.
    if (this._qcColumnKey !== undefined) {
      const qcKey = this.currentQcColumnKey();
      if (qcKey !== this._qcColumnKey) {
        this._qcColumnKey = qcKey;
        this._columnsData = this.getColumns();
        this.updateCols();
      }
    }

    this.requestUpdate();
  }

  @monitor('_expandedOptions', { delayUntilFirstUpdate: true })
  public async handleExpandCollapse() {
    if (!this.table) return;

    this.quoteManager.container.items?.forEach(x => {
      const rowItem = x;

      const isShown = this.table!.isExpanded(rowItem);

      if (this._expandedOptions && !isShown)
        this.table!.addExtension(rowItem, this._extendedDetailsTemplateGenerator(rowItem.id));
      else if (!this._expandedOptions && isShown) this.table!.remExtension(rowItem);
    });

    // The expand-all indicator icon lives in the table header (rendered inside
    // the table's shadow DOM); it is driven reactively from _expandedOptions in
    // the column header template, so no manual icon update is needed here.
  }

  quoteCalculationsEnabled() {
    return this.quoteManager.hasQuoteCalculationData;
  }

  getQuoteItemTags(quoteItemContainer: QuoteItemContainer) {
    const validationIndicator = () => {
      const icon = frameValidationIcon(this.getItemValidationType(quoteItemContainer));
      if (icon) return html` ${icon}`;
      if (!quoteItemContainer.price.isTaxableItem) {
        return html` <webmodule-icon library="default" name="no-tax"></webmodule-icon>`;
      }
      return html``;
    };
    const notesIndicator = () => {
      if (quoteItemContainer.item.comment)
        return html` <webmodule-icon library="fa" name="fas-message" color="green"></webmodule-icon>`;
      else return html``;
    };
    const notesManufacturerIndicator = () => {
      if (quoteItemContainer.item.commentToManufacturer)
        return html` <webmodule-icon library="fa" name="fas-message" color="black"></webmodule-icon>`;
      else return html``;
    };

    const supplierPriceAdjIndicator = () => {
      if (
        quoteItemContainer.price.supplierPriceAdjustmentEnabled &&
        quoteItemContainer.price.supplierPriceAdjustment != 0
      )
        return html` <webmodule-icon library="fa" name="fas-tags"></webmodule-icon>`;
      else return html``;
    };

    const dealerPriceAdjIndicator = () => {
      if (quoteItemContainer.price.priceAdjustment !== 0)
        return html` <webmodule-icon library="fa" name="fas-tags" color="primary"></webmodule-icon>`;
      else return html``;
    };

    return html`${supplierPriceAdjIndicator()}${dealerPriceAdjIndicator()}${notesIndicator()}${notesManufacturerIndicator()}${validationIndicator()}`;
  }

  generateQuoteItemOptionsView(quoteItemContainer: QuoteItemContainer, childVisible: boolean) {
    // The cell renders in the table's shadow DOM, so the legacy Font Awesome <i>
    // glyph never resolves (the global FA font-face/CSS does not cross the shadow
    // boundary); use webmodule-icon, which renders the icon in its own shadow root.
    const caret = childVisible ? 'fas-caret-up' : 'fas-caret-down';
    return html`<span>${this.getOptionsOneLiner(quoteItemContainer)}</span
      ><span class="options-dropdown"><webmodule-icon library="fa" name=${caret}></webmodule-icon></span>`;
  }

  getItemValidationType(quoteItemContainer: QuoteItemContainer): FrameValidationType {
    //we are currently only supporting items that are for v6
    if (!isV6(quoteItemContainer)) return FrameValidationType.nothing;

    const data = getV6FrameForContainer(quoteItemContainer);
    if (!data) {
      if (isFrame(quoteItemContainer.item)) return FrameValidationType.critical;
      return FrameValidationType.nothing;
    }

    if (this.quoteManager.currentStateRequiresValidation) {
      if (this.quoteManager.isFrameOutOfDate(quoteItemContainer, [], true)) return FrameValidationType.outOfDate;
    }
    // no validations on the frame, no point trying to find the highest ranked
    if (data.validations?.length == 0) {
      return FrameValidationType.nothing;
    }

    return rankToValidationType(highestValidationRank(data.validations));
  }

  getOptionsOneLiner(quoteItemContainer: QuoteItemContainer) {
    //We should only progress if item is a configuration from supplier.
    if (!isV6(quoteItemContainer)) return '';
    //We are only here if we are a v6 Frame
    const data = getV6QuoteItemForContainer(quoteItemContainer);
    return getV6QuoteItemSubtitleOrFrameDimensions(data);
  }

  ellipsisMenu(quoteItem: QuoteItem) {
    const clickOpen = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.openItem(quoteItem);
    };

    const clickCopy = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.copyItem(quoteItem);
    };

    const clickDelete = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.deleteItem(quoteItem);
    };

    const clickUnDelete = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.undeleteItem(quoteItem);
    };

    const clickProperties = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.propertiesItem(quoteItem);
    };
    const clickBomViewer = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();

      await this.showBomView(quoteItem);
    };

    const canShowBom =
      (isCynclyStaff() || isSupplierAgent() || isDebugMode()) &&
      v6SupportsVersion(v6VersionMap.hasBomView) &&
      isFrame(quoteItem);

    const canEditPricing = this.quoteManager.canUpdatePricingMetric;

    // For dealer price adjustment, shipping, or freehand items in readonly mode, show a view button
    // instead of a dropdown menu since there's effectively only one action available.
    if (
      (isDealerQuotePriceAdjustment(quoteItem) || isShipping(quoteItem) || isAnyFreehand(quoteItem)) &&
      this.quoteManager.isReadonly()
    ) {
      const clickHandler = isDealerQuotePriceAdjustment(quoteItem) ? clickProperties : clickOpen;
      return html`<webmodule-icon-button
        class="item-menu"
        library="fa"
        name="fas-eye"
        @click=${clickHandler}
      ></webmodule-icon-button>`;
    }

    return html` <webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        ${when(
          canShowBom,
          () =>
            html` <webmodule-menu-item @click=${clickBomViewer}>
                <webmodule-icon slot="prefix" library="fa" name="fas-list-check"></webmodule-icon>
                ${LangFranchiseeQuoteItemsView.bomView()}
              </webmodule-menu-item>
              <webmodule-divider></webmodule-divider>`
        )}
        ${when(
          this.quoteManager.isReadonly() || isShipping(quoteItem) || isSSI(quoteItem),
          () => html``,
          () =>
            quoteItem.state !== QuoteItemState.Deleted
              ? html` <webmodule-menu-item @click=${clickDelete}>
                  <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.delete()}
                </webmodule-menu-item>`
              : html` <webmodule-menu-item @click=${clickUnDelete}>
                  <webmodule-icon slot="prefix" library="fa" name="fas-trash-arrow-up"></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.undelete()}
                </webmodule-menu-item>`
        )}
        ${when(
          this.quoteManager.isReadonly() ||
            isAnyFreehand(quoteItem) ||
            isShipping(quoteItem) ||
            isSpecialItem(quoteItem) ||
            isSSI(quoteItem),
          () => html``,
          () =>
            html` <webmodule-menu-item @click=${clickCopy}>
              <webmodule-icon slot="prefix" library="fa" name="fas-copy"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.copy()}
            </webmodule-menu-item>`
        )}
        ${this.moveToGroupMenuTemplate(quoteItem)}
        ${when(
          isSSI(quoteItem) || isDealerQuotePriceAdjustment(quoteItem),
          () => html``,
          () =>
            html` <webmodule-menu-item @click=${clickOpen}>
              <webmodule-icon
                slot="prefix"
                library="fa"
                name=${this.quoteManager.isReadonly() ? 'fas-eye' : 'fas-pen-to-square'}
              ></webmodule-icon>
              ${this.quoteManager.isReadonly()
                ? LangFranchiseeQuoteItemsView.view()
                : LangFranchiseeQuoteItemsView.edit()}
            </webmodule-menu-item>`
        )}
        ${when(
          canEditPricing,
          () => html`
            <webmodule-menu-item @click=${clickProperties}>
              <webmodule-icon slot="prefix" library="fa" name="fas-sliders"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.pricing()}
            </webmodule-menu-item>
          `
        )}
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  /**
   * Close the row / group-header menu an action was invoked from.
   *
   * Every action handler calls stopPropagation so the click cannot reach the row beneath it — which also
   * stops `webmodule-dropdown` seeing the select event it closes itself on (it has no `stay-open-on-select`).
   * Handlers that open a dialog appear to work because the dialog covers the stale menu; the ones that act
   * inline, like Move to Group and the group Move Up / Move Down, visibly leave it hanging until the user
   * clicks somewhere else. Menu items are slotted into the dropdown from this component's own template, so
   * they are in one tree and `closest` reaches it.
   */
  private closeMenuFrom(e: Event): void {
    const dropdown = (e.target as HTMLElement | null)?.closest('webmodule-dropdown') as
      | (HTMLElement & { hide?: () => Promise<void> })
      | null;
    void dropdown?.hide?.();
  }

  /**
   * "Move to Group" submenu on a quote item row: lists the existing groups (checking the current
   * one), offers removal from the current group and creating a new group with the item.
   */
  private moveToGroupMenuTemplate(quoteItem: QuoteItem) {
    if (
      this.quoteManager.isReadonly() ||
      this.quoteManager.itemIsNotSortable(quoteItem) ||
      quoteItem.state === QuoteItemState.Deleted
    )
      return html``;

    const doc = this.quoteManager.quoteItemGrouping;
    if (!doc) return html``;
    const groups = getGroups(doc);
    const currentGroupId = this._groupIdByCommonId.get(quoteItem.commonId) ?? null;

    const clickMove = (groupId: string | null) => async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFrom(e);
      if (groupId !== currentGroupId) await this.moveItemToGroupAction(quoteItem, groupId);
    };
    const clickNewGroup = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFrom(e);
      await this.createGroupAction(quoteItem);
    };

    return html` <webmodule-menu-item>
      <webmodule-icon slot="prefix" library="fa" name="fas-layer-group"></webmodule-icon>
      ${LangFranchiseeQuoteItemsView.moveToGroup()}
      <webmodule-menu slot="submenu">
        ${groups.map(
          group =>
            html` <webmodule-menu-item
              type="checkbox"
              ?checked=${group.id === currentGroupId}
              @click=${clickMove(group.id)}
            >
              ${group.name}
            </webmodule-menu-item>`
        )}
        ${when(
          currentGroupId !== null,
          () =>
            html` <webmodule-menu-item @click=${clickMove(null)}>
              <webmodule-icon slot="prefix" library="fa" name="fas-arrow-right-from-bracket"></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.removeFromGroup()}
            </webmodule-menu-item>`
        )}
        ${when(groups.length > 0 || currentGroupId !== null, () => html`<webmodule-divider></webmodule-divider>`)}
        <webmodule-menu-item @click=${clickNewGroup}>
          <webmodule-icon slot="prefix" library="fa" name="fas-plus"></webmodule-icon>
          ${LangFranchiseeQuoteItemsView.newGroup()}
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-menu-item>`;
  }

  /**
   * Resolve an item's group for the new table's native grouping. Items not in a
   * group return null, which the table renders as plain (ungrouped) rows with no
   * header band - so the forced-to-bottom non-groupable items stay headerless.
   */
  private _groupKeyFor = (item: object): string | null => {
    return this._groupIdByCommonId.get((item as QuoteItem).commonId) ?? null;
  };

  /**
   * Native group-header content. The table renders the surrounding sticky header
   * row (and handles drop highlighting + the move event); this supplies the
   * title, badges and the group actions menu. `groupValue` is the group id.
   */
  private _groupHeaderTemplate = (groupValue: unknown): TemplateResult => {
    const header = this._headerById.get(String(groupValue));
    if (!header) return html``;

    const excludedBadge = header.attributes.excludeFromPricing
      ? html`<span class="badge quote-item-group-badge">${LangFranchiseeQuoteItemsView.excludedFromPricing()}</span>`
      : nothing;
    // The primary batch can be renamed and moved like any other - it is marked by attribute, not by trunk
    // position - so neither its name nor its place tells the dealer that this is where new items land. Say
    // so explicitly. The explanation is a NATIVE title, not the shared getToolTip affordance: that renders a
    // hoisted popup, and inside this sticky group-header cell the popup is clipped by the cell's containing
    // block and disappears behind the table (the group menu escapes only because the table renders it in a
    // separate pinned region). A native title is drawn by the browser and cannot be clipped or covered.
    const defaultBadge =
      header.attributes[primaryBatchAttributeKey] === true
        ? html`<span class="badge quote-item-group-badge" title=${LangFranchiseeQuoteItemsView.defaultBatchTooltip()}
            >${LangFranchiseeQuoteItemsView.defaultBatch()}</span
          >`
        : nothing;
    // In V6 batch mode a group is a delivery batch: surface its requested delivery date / customer reference
    // inline as a muted fast-view (each shown only when set). The dialog still holds the full detail.
    const batchDetail = this.quoteManager.isV6BatchLinkingEnabled
      ? renderBatchHeaderDetail([
          {
            label: LangFranchiseeQuoteItemsView.batchDeliveryDate(),
            value: batchHeaderDate(header.attributes.deliveryDate)
          },
          {
            label: LangFranchiseeQuoteItemsView.batchCustomerRef(),
            value: header.attributes.customerRef
          }
        ])
      : nothing;
    return html`<span class="quote-item-group-title">
      <webmodule-icon library="fa" name="fas-layer-group"></webmodule-icon>
      <span class="quote-item-group-name">${header.name}</span>
      <span class="badge quote-item-group-badge"
        >${LangFranchiseeQuoteItemsView.groupItemCount({ count: header.itemCommonIds.length })}</span
      >
      ${defaultBadge} ${excludedBadge} ${batchDetail}
    </span>`;
  };

  // Group "..." menu - rendered in the table's pinned group-action region so it
  // stays at the visible right edge instead of scrolling off with the wide header.
  private _groupActionTemplate = (groupValue: unknown): TemplateResult => {
    const header = this._headerById.get(String(groupValue));
    if (!header) return html``;
    return html`<span class="quote-item-group-menu">${this.groupEllipsisMenu(header)}</span>`;
  };

  /**
   * actions menu for a group header. Group actions are handled locally (the table owns the
   * quoteManager) rather than via the wm-quote-item-action event, which expects a quote item
   * container that groups do not have.
   */
  groupEllipsisMenu(group: QuoteItemGroupHeader) {
    // Read-only quote: nothing is editable, but when the supplier's V6 supports batches the group carries
    // batch metadata (delivery date / customer ref / comment) worth seeing - offer a read-only View. With no
    // batch linking there is nothing batch-specific to view, so keep the header menu-less as before.
    if (this.quoteManager.isReadonly()) {
      if (!this.quoteManager.isV6BatchLinkingEnabled) return html``;

      const clickView = async (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        await this.editGroupAction(group);
      };
      return html` <webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
        <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
        <webmodule-menu>
          <webmodule-menu-item @click=${clickView}>
            <webmodule-icon slot="prefix" library="fa" name="fas-eye"></webmodule-icon>
            ${LangFranchiseeQuoteItemsView.viewGroup()}
          </webmodule-menu-item>
        </webmodule-menu>
      </webmodule-dropdown>`;
    }

    const doc = this.quoteManager.quoteItemGrouping;
    const rootIndex = doc?.root.findIndex(node => node.type === 'group' && node.id === group.id) ?? -1;
    // the trunk reads loose items, then the groups, then the quote-level charges - so first/last has to be
    // relative to the other GROUPS, not to the trunk's node count
    const groups = doc ? getGroups(doc) : [];
    const groupIndex = groups.findIndex(x => x.id === group.id);
    const isFirst = groupIndex <= 0;
    const isLast = groupIndex < 0 || groupIndex >= groups.length - 1;

    const isPrimary = group.attributes[primaryBatchAttributeKey] === true;

    const clickEdit = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFrom(e);
      await this.editGroupAction(group);
    };
    const clickMoveUp = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFrom(e);
      if (!isFirst) await this.moveGroupAction(group, rootIndex - 1);
    };
    const clickMoveDown = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFrom(e);
      if (!isLast) await this.moveGroupAction(group, rootIndex + 1);
    };
    const clickDelete = async (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.closeMenuFrom(e);
      await this.deleteGroupAction(group);
    };

    return html` <webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        <webmodule-menu-item @click=${clickEdit}>
          <webmodule-icon slot="prefix" library="fa" name="fas-pen-to-square"></webmodule-icon>
          ${LangFranchiseeQuoteItemsView.editGroup()}
        </webmodule-menu-item>
        <webmodule-menu-item ?disabled=${isFirst} @click=${clickMoveUp}>
          <webmodule-icon slot="prefix" library="fa" name="fas-arrow-up"></webmodule-icon>
          ${LangFranchiseeQuoteItemsView.moveUp()}
        </webmodule-menu-item>
        <webmodule-menu-item ?disabled=${isLast} @click=${clickMoveDown}>
          <webmodule-icon slot="prefix" library="fa" name="fas-arrow-down"></webmodule-icon>
          ${LangFranchiseeQuoteItemsView.moveDown()}
        </webmodule-menu-item>
        ${
          // The primary batch is the catch-all every unbatched item lives in, so it has no Delete: batching
          // is one-way and removing it would leave its items nowhere to go. Everything else about it is a
          // normal batch, so Edit and the reorder actions stay.
          isPrimary
            ? nothing
            : html`<webmodule-divider></webmodule-divider>
                <webmodule-menu-item @click=${clickDelete}>
                  <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.deleteGroup()}
                </webmodule-menu-item>`
        }
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  private async editGroupAction(group: QuoteItemGroupHeader) {
    const readonly = this.quoteManager.isReadonly();
    const result = await QuoteItemGroupDialog.Execute(
      {
        name: group.name,
        excludeFromPricing: group.attributes.excludeFromPricing,
        deliveryDate: group.attributes.deliveryDate ?? null,
        customerRef: group.attributes.customerRef ?? null,
        comment: group.attributes.comment ?? null
      },
      'edit',
      this.quoteManager.isV6BatchLinkingEnabled,
      readonly
    );
    // A read-only view returns null (no Save), and nothing must be written back even if it did.
    if (!result || readonly) return;
    if (
      await this.quoteManager.updateItemGroup(group.id, result.name, {
        excludeFromPricing: result.excludeFromPricing,
        // An untouched date input returns '' rather than null; store null so the blob never carries a
        // value the API cannot deserialize into DateTimeOffset?.
        deliveryDate: normalizeBatchDate(result.deliveryDate),
        customerRef: result.customerRef ?? null,
        comment: result.comment ?? null
      })
    )
      await this.refreshData();
  }

  private async moveGroupAction(group: QuoteItemGroupHeader, trunkIndex: number) {
    if (await this.quoteManager.moveItemGroup(group.id, trunkIndex)) await this.refreshData();
  }

  private async deleteGroupAction(group: QuoteItemGroupHeader) {
    if (
      !(await AskConfirmation(
        LangFranchiseeQuoteItemsView.deleteGroupConfirm({ name: group.name }),
        buttonsContinueCancel()
      ))
    )
      return;
    if (await this.quoteManager.deleteItemGroup(group.id)) await this.refreshData();
  }

  /** create a group via the dialog and move the item into it right away */
  private async createGroupAction(item: QuoteItem): Promise<boolean> {
    const doc = await this.quoteManager.needsItemGrouping();
    const defaultName = LangFranchiseeQuoteItemsView.groupDefaultName({ number: getGroups(doc).length + 1 });
    const result = await QuoteItemGroupDialog.Execute(
      { name: defaultName, excludeFromPricing: false },
      'create',
      this.quoteManager.isV6BatchLinkingEnabled
    );
    if (!result) return false;
    const groupId = await this.quoteManager.createItemGroup(
      result.name,
      {
        excludeFromPricing: result.excludeFromPricing,
        // As above: '' from an empty date input must not reach the blob or the API.
        deliveryDate: normalizeBatchDate(result.deliveryDate),
        customerRef: result.customerRef ?? null,
        comment: result.comment ?? null
      },
      // Creating the first batch also creates the catch-all that every other item moves into.
      LangFranchiseeQuoteItemsView.primaryBatchName(),
      // Place the item in the SAME mutation. Moving it in a second call made the quote go from "no batches"
      // to "batched" (which runs SSI immediately, by design) and then immediately look like a composition
      // change on an already-batched quote, which defers - so creating a first group always landed deferred
      // despite the code that exists to make exactly that case seamless.
      item.commonId
    );
    if (!groupId) return false;
    await this.refreshData();
    return true;
  }

  private async moveItemToGroupAction(item: QuoteItem, groupId: string | null) {
    const doc = this.quoteManager.quoteItemGrouping;
    const targetEnd =
      groupId === null ? (doc?.root.length ?? 0) : doc ? (findGroup(doc, groupId)?.children.length ?? 0) : 0;
    if (await this.quoteManager.moveItemToGroup(item.commonId, groupId, targetEnd)) await this.refreshData();
  }

  async showBomView(quoteItem: QuoteItem) {
    const container = this.quoteManager.quoteItemContainer(quoteItem.id);
    const v6Data = getV6QuoteItemForContainer(container);
    const url = this.quoteApi.convertQuoteItemAssetPathToUrl(
      quoteItem.quoteId,
      quoteItem.id,
      quoteItem.virtualThumbnailPath,
      thumbnailTag(this.quoteManager, quoteItem)
    );
    const quoteTitle = getQuoteTitleStr(this.quoteManager.quote);
    const itemTitle = quoteItem.description;
    const itemPos = this.quoteManager.itemPosition(container.item.id);
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const title = `${quoteTitle} - #${itemPos} ${itemTitle}`;
    this.quoteManager.quote;
    const viewer = await constructAsync(
      new V6BomViewer(title, {
        id: quoteItem.id,
        quantity: quoteItem.quantity,
        quoteItem: v6Data,
        thumbnailURL: url,
        fetchAsset: async (assetType: QuoteItemAssetType) => {
          if (assetType === 'bomView') {
            const bomViewData = (v6Data as any).bomView;
            if (bomViewData) {
              return bomViewData;
            }

            const filePath = container.data?.assetVirtualPaths['bomView']?.path;
            const publicUrl = container.data?.assetVirtualPaths['bomView']?.publicUrl;
            if (!filePath) {
              return null;
            }
            //support backwards compatibility where bomView may be directly on v6Data
            return await fetchQuoteAsset({
              quoteId: quoteItem.quoteId,
              quoteItemId: quoteItem.id,
              assetPath: filePath,
              asJsonObject: true,
              publicUrl: publicUrl
            });
          }
          return null;
        }
      })
    );

    await viewer.showModal();
  }

  _extendedDetailsTemplateGenerator = (id: string) => async () => {
    const container = await this.quoteManager.quoteItemContainer(id);
    return html`<franchisee-quote-item-expander
      .quoteManager=${this.quoteManager}
      .container=${container}
      .columns=${this._columns}
    ></franchisee-quote-item-expander>`;
  };

  protected async firstUpdated(_changedProperties: PropertyValues) {
    await this.refreshData();
    this._columnsData = this.getColumns();
    this.updateCols();
    this._qcColumnKey = this.currentQcColumnKey();
  }

  protected render() {
    const keyEvent = (item: QuoteItem) => {
      return item.id;
    };

    const rowIdEvent = (item: QuoteItem) => {
      return item.id;
    };

    const itemRowClassEvent = (item: QuoteItem) => {
      const groupedClass = this._groupIdByCommonId.has(item.commonId) ? ' quote-item-grouped' : '';
      if (item.state === QuoteItemState.Deleted) {
        return 'quote-item-deleted';
      }
      if (!isSpecialItem(item)) return groupedClass.trim();
      const bqs = this.quoteManager.branchQuoteSupport;
      const link = bqs.conversationLinks.find(x => x.quoteItemId === item.id);
      if (!link) return groupedClass.trim();
      const support = bqs.items.find(x => x.conversationId === link.conversationId);
      if (!support) return groupedClass.trim();

      return `quotesupport-status-${BranchQuoteSupportStatus[support.status].toLowerCase()}${groupedClass}`;
    };

    const enableDragEvent = (item: QuoteItem): boolean => {
      return !(item && (isSSI(item) || isShipping(item)) && item.state !== QuoteItemState.Deleted);
    };

    return html` <webmodule-table
      id="quote-items-table"
      class="quote-items-table"
      fill-height
      .elevated=${false}
      .cellBorders=${false}
      .uppercaseHeader=${false}
      .stickyFooter=${false}
      .keyevent=${keyEvent}
      .itemRowClassEvent=${itemRowClassEvent}
      .rowidevent=${rowIdEvent}
      .checkEnabledDragEvent=${enableDragEvent}
      .columns=${this._columns}
      pageLength="100"
      .data=${this._data}
      .clickrows=${false}
      .getGroupByKey=${this._groupKeyFor}
      .groupHeaderTemplate=${this._groupHeaderTemplate}
      .groupActionTemplate=${this._groupActionTemplate}
      .emptyGroups=${this._emptyGroups}
      .groupDropPosition=${'end'}
      .customStyles=${quoteItemsTableStyles}
      @webmodule-table-item-move=${this._handleMove}
      .enableDrag=${!this.quoteManager.isReadonly()}
    >
    </webmodule-table>`;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  /**
   * interpret a row move against the grouping document: the dragged item lands at the target
   * item's position within the target's container (trunk or group) - covering reorder within the
   * trunk, reorder within a group, moving into a group (drop onto a member row) and moving back
   * out to the trunk. The new table also reports drops onto a group header directly (via
   * `targetGroupKey`), which moves the item into that group; groups reorder via their menu.
   */
  private async _handleMove(e: CustomEvent<WebModuleTableItemMove>) {
    const doc = this.quoteManager.quoteItemGrouping;
    const { sourceIndex, targetIndex, targetGroupKey, dropPosition } = e.detail;
    const source = this._data[sourceIndex];
    if (!source) return;

    // Dropped onto a group header: move the item into that group. `dropPosition`
    // ('start'/'end') is set by the table's groupDropPosition.
    if (targetGroupKey !== undefined && targetGroupKey !== null) {
      if (!doc) return;
      if (source.state === QuoteItemState.Deleted || this.quoteManager.itemIsNotSortable(source)) return;
      const group = findGroup(doc, targetGroupKey);
      const endIndex = group?.children.length ?? 0;
      const index = dropPosition === 'start' ? 0 : endIndex;
      if (await this.quoteManager.moveItemToGroup(source.commonId, targetGroupKey, index)) await this.refreshData();
      return;
    }

    if (!doc) {
      // legacy path: no grouping document, plain flat reorder
      const newData = this.moveArrayItem([...this._data], e.detail).map(x => x.commonId);

      if (await this.quoteManager.updateItemSortOrder(newData)) {
        await this.requestUpdate();
      }
      return;
    }

    const target = this._data[targetIndex];
    if (!target || source === target) return;
    if (source.state === QuoteItemState.Deleted || this.quoteManager.itemIsNotSortable(source)) return;
    if (target.state === QuoteItemState.Deleted) return;

    const targetGroupId = this._groupIdByCommonId.get(target.commonId) ?? null;

    // A batch's supplier charges (shipping / SSI) are pinned to the BOTTOM of the batch, so the row a user
    // naturally aims at to append to a group is a non-sortable one. Rejecting the drop outright made a
    // group look like it could be dragged out of but never into. The charge is not a reorder target, but it
    // does identify a container: land the item at the end of that container's own items instead.
    if (this.quoteManager.itemIsNotSortable(target)) {
      const container = targetGroupId === null ? doc.root : (findGroup(doc, targetGroupId)?.children ?? []);
      const endIndex = container.filter(node => node.type === 'item').length;
      if (await this.quoteManager.moveItemToGroup(source.commonId, targetGroupId, endIndex)) await this.refreshData();
      return;
    }

    const index = this.ordinalWithinContainer(doc, target, targetGroupId);
    if (await this.quoteManager.moveItemToGroup(source.commonId, targetGroupId, index)) await this.refreshData();
  }

  /**
   * index of the target item within its container (trunk or group). The data-layer moveItem
   * removes the source first and then inserts at this index - the same splice semantics as the
   * legacy flat reorder (dragging downwards lands after the target, upwards before it).
   */
  private ordinalWithinContainer(
    doc: QuoteItemGroupingDocument,
    target: QuoteItem,
    targetGroupId: string | null
  ): number {
    const container = targetGroupId === null ? doc.root : (findGroup(doc, targetGroupId)?.children ?? []);
    const index = container.findIndex(node => node.type === 'item' && node.id === target.commonId);
    return index < 0 ? container.length : index;
  }

  private moveArrayItem<T>(array: T[], moveData: WebModuleTableItemMove): T[] {
    const { sourceIndex, targetIndex } = moveData;

    // Ensure the indices are within the bounds of the array
    if (
      sourceIndex < 0 ||
      sourceIndex >= array.length ||
      targetIndex < 0 ||
      targetIndex >= array.length ||
      sourceIndex === targetIndex
    ) {
      throw new Error('Invalid indices provided');
    }

    // Remove the item from the original position
    const item = array.splice(sourceIndex, 1)[0];

    // Insert the item at the new position
    array.splice(targetIndex, 0, item);

    return array;
  }

  private async openItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.edit
    });
  }

  private async copyItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.copy
    });
  }

  private async deleteItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.delete
    });
  }
  private async undeleteItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.undelete
    });
  }

  private async propertiesItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.properties
    });
  }

  private async openFabricationJobPicker(quoteItemId: string): Promise<void> {
    const selected = await fabricationJobPicker(userDataStore.defaultBranch.id);
    if (!selected) return;

    const api = getApiFactory().fabricationJob();
    const result = await api.getFabricationJob({ id: selected.id });
    if (!result) return;

    await api.addQuoteItemToFabricationJob({
      fabricationJobId: selected.id,
      quoteItemId,
      fabricationJobRecordVersion: result.fabricationJob.recordVersion
    });

    await this.quoteManager.refreshItemFabricationStatuses();
  }

  /**
   * Every Ready-for-Delivery item on this quote, as checklist rows. Built from the same data the
   * grid already renders, so numbers and thumbnails match what the user is looking at.
   */
  private deliveryCandidates(): DeliveryCandidate[] {
    return this.deliveryCandidatesFor(
      id =>
        this.quoteManager.itemFabricationStatuses.get(id)?.fabricationStatus ===
        QuoteItemFabricationStatus.ReadyForDelivery
    );
  }

  private deliveryCandidatesFor(include: (quoteItemId: string) => boolean): DeliveryCandidate[] {
    return (this.quoteManager.container.items ?? [])
      .filter(qi => include(qi.id))
      .map(qi => {
        // Options and tags come from the grid's own renderers, so the checklist reads exactly
        // like the row the user clicked. Both need the item's container; a missing one just
        // leaves those cells empty rather than failing the whole list.
        let container;
        try {
          container = this.quoteManager.quoteItemContainer(qi.id);
        } catch {
          container = undefined;
        }
        return {
          quoteItemId: qi.id,
          itemNumber: this._itemNumbers.get(qi.commonId) ?? '',
          thumbnailUrl:
            qi.virtualThumbnailPath === ''
              ? quoteItemThumbSrc(qi)
              : this.quoteApi.convertQuoteItemAssetPathToUrl(
                  qi.quoteId,
                  qi.id,
                  qi.virtualThumbnailPath,
                  thumbnailTag(this.quoteManager, qi)
                ),
          location: qi.title || qi.id.substring(0, 8),
          description: qi.description,
          options: container ? this.getOptionsOneLiner(container) : '',
          tags: container ? this.getQuoteItemTags(container) : undefined,
          trackingReference: this.quoteManager.itemFabricationStatuses.get(qi.id)?.factoryTrackingReference ?? '',
          quantity: qi.quantity
        };
      });
  }

  /** Clicking a Ready-for-Delivery badge: pick which of the quote's ready items are going out. */
  private async recordDeliveryFrom(quoteItemId: string): Promise<void> {
    const entered = await RecordDeliveryDialog.Execute(
      { deliveryDate: localDateToRFC3339(new Date()), notes: '' },
      [quoteItemId],
      this.deliveryCandidates()
    );
    if (!entered || entered.quoteItemIds.length === 0) return;
    const result = await getApiFactory().fabricationJob().recordQuoteItemDelivery({
      quoteItemIds: entered.quoteItemIds,
      deliveryDate: entered.deliveryDate,
      notes: entered.notes
    });
    if (!result) return;
    fireQuickSuccessToast(LangFabricationJobActions.deliveryRecordedToast(entered.quoteItemIds.length));
    await this.quoteManager.refreshItemFabricationStatuses();
  }

  /**
   * Clicking a Delivered badge: show that delivery, read-only. Every item of the same batch is
   * listed, not just the one clicked, so the dialog answers "what went out together". Editing a
   * recorded delivery is not a requirement and is deliberately not offered.
   */
  private async viewDeliveryFor(quoteItemId: string): Promise<void> {
    const fab = this.quoteManager.itemFabricationStatuses.get(quoteItemId);
    if (!fab?.deliveredDate) return;
    const batchId = fab.deliveryBatchId;
    const inBatch = new Set(
      [...this.quoteManager.itemFabricationStatuses.values()]
        .filter(s => isSameDeliveryBatch(s.deliveryBatchId, s.quoteItemId, batchId, quoteItemId))
        .map(s => s.quoteItemId)
    );
    await RecordDeliveryDialog.View(
      { deliveryDate: serverDateToLocalRFC3339(fab.deliveredDate), notes: fab.deliveryNotes ?? '' },
      this.deliveryCandidatesFor(id => inBatch.has(id))
    );
  }

  /**
   * The distinct set of quote-calculation (NFRC / energy-rating) columns to show,
   * discovered from the current quoteCalculations. Returns only the column identity
   * (name/caption) — never the per-row values, which are read live at render time in
   * getColumns() so reconfigured items stay current (Bug 229662). Kept as its own
   * method so the value snapshot can't leak into a column's displayValue closure.
   */
  private discoverQuoteCalcColumns(): QCValue[] {
    const colDefs: QCValue[] = [];
    const qcdata = this.quoteManager.quoteCalcuationData as QCQuoteValues | undefined;
    if (!qcdata) return colDefs;
    qcdata.quoteItemValues.forEach(x => {
      x.values.forEach(col => {
        if (col.displayColumn && colDefs.find(x1 => x1.name === col.name) === undefined) colDefs.push(col);
      });
    });
    return colDefs;
  }

  /** Stable signature of the current quote-calculation column set (by name), used to
   * detect when an SSI round-trip added or removed NFRC columns. */
  private currentQcColumnKey(): string {
    return this.discoverQuoteCalcColumns()
      .map(c => c.name)
      .join('|');
  }

  private getColumns(): WebModuleTableColumnDef[] {
    const cols: WebModuleTableColumnDef[] = [];

    const qCurrency = this.quoteManager.quoteCurrency;

    const addColumnDef = (columnId: string, column: WebModuleTableColumnDef) => {
      cols.push({ ...column, id: columnId, sortable: false });
    };

    // Per-group total: sum a per-item money value across the group's member rows.
    // Mirrors the grand-total footer columns at the group level.
    const sumGroup = (items: object[], perItem: (id: string) => number): number =>
      (items as QuoteItem[]).reduce((total, it) => total + (perItem(it.id) || 0), 0);

    // Every money total on this table - grand total, per-group total, the gross sub-total - is summed over
    // rows that INCLUDE the supplier-computed (SSI) lines. While pricing is deferred (Task 230006) those
    // lines are stale and render as "Deferred" in the price column, so printing a figure here states a
    // total the quote cannot stand behind, sitting directly under rows that admit they are unknown.
    // Anything downstream of a deferment shows the deferment instead.
    //
    // No tooltip on the badge here: the price cells above already carry the explanation, and repeating a
    // question-mark affordance across six total columns is noise, not help.
    const deferrable = (total: () => TemplateResult): TemplateResult =>
      this.quoteManager.ssiDeferred
        ? html`<span class="badge badge-warning">${LangFranchiseeQuoteItemsView.pricingUnavailable()}</span>`
        : total();

    addColumnDef('number', {
      title: () => LangFranchiseeQuoteItemsView.itemNumber(),
      classes: 'quote-item-no',
      width: '72px',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;
        // Read from the field (refreshed every refreshData) so moves renumber even when columns don't rebuild.
        const num = this._itemNumbers.get(rowItem.commonId) ?? '';

        if (rowItem.isRestrictedToPowerUser) return html`${num} <span class="power-user"></span>`;
        return html`${num}`;
      }
    });
    addColumnDef('thumbnail', {
      title: () => LangFranchiseeQuoteItemsView.image(),
      classes: 'quote-item-image',
      width: '80px',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const row = item as QuoteItem;

        const src =
          row.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(row)
            : this.quoteApi.convertQuoteItemAssetPathToUrl(
                row.quoteId,
                row.id,
                row.virtualThumbnailPath,
                thumbnailTag(this.quoteManager, row)
              );

        const imagePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();

          if (isFrame(row) || !isEmptyOrSpace(row.virtualThumbnailPath))
            this.imagePreview?.showImagePreview(
              this.quoteApi.convertQuoteItemAssetPathToUrl(
                row.quoteId,
                row.id,
                row.virtualThumbnailPath,
                thumbnailTag(this.quoteManager, row)
              ),
              'quote-item-thumb'
            );
        };

        const hidePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.imagePreview?.hideImagePreview();
        };

        return html` <div
          @mouseenter=${imagePreview}
          @mouseleave=${hidePreview}
          @touchstart=${imagePreview}
          @touchend=${hidePreview}
          @touchcancel=${hidePreview}
        >
          <img
            class="quote-item-thumbnail"
            alt="Thumbnail for quote item"
            src="${src}"
            style="width:48px; height:48px"
          />
        </div>`;
      }
    });
    addColumnDef('location', {
      title: () => LangFranchiseeQuoteItemsView.frameTitleAndDescription(),
      classes: 'quote-item-title',
      // The title/description column is the flexible one: it grows to absorb the
      // slack and floors at a min width before the table scrolls horizontally.
      minWidth: '200px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        const clickEvent = async (e: Event) => {
          e.stopPropagation();
          e.preventDefault();

          await this.openItem(rowItem);
        };

        const title = isEmptyOrSpace(rowItem.title) ? LangFranchiseeQuoteItemsView.frameTitle() : rowItem.title;
        return html`<a class="quote-item-link" href="#" @click="${clickEvent}"
            >${firstValidString(title, LangFranchiseeQuoteItemsView.untitled())}</a
          >
          <span class="quote-item-description">${rowItem.description}</span>`;
      },
      // Footer summary-row labels: first row = items total, second row = overall quote total.
      columnAggregate: () => LangFranchiseeQuoteItemsView.itemsTotal(),
      columnSubAggregate: () => LangFranchiseeQuoteItemsView.quoteTotal(),
      // Per-group footer label (the totals appear in the money columns).
      groupAggregate: () => html`<strong>${LangFranchiseeQuoteItemsView.groupTotal()}</strong>`
    });

    addColumnDef('options', {
      title: () => LangFranchiseeQuoteItemsView.options(),
      // Expand/collapse-all lives in the header actions region; the icon is driven
      // reactively from _expandedOptions (it renders in the table's shadow DOM).
      headerActions: () =>
        html`<webmodule-icon-button
          library="fa"
          name=${this._expandedOptions ? 'fas-angles-up' : 'fas-angles-down'}
          title=${LangFranchiseeQuoteItemsView.options()}
          @click=${() => (this._expandedOptions = !this._expandedOptions)}
        ></webmodule-icon-button>`,
      classes: 'quote-item-description',
      width: '200px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        try {
          const container = this.quoteManager.quoteItemContainer(rowItem.id);

          return this.generateQuoteItemOptionsView(container, _table.isExpanded(rowItem) != undefined);
        } catch {
          return html``;
        }
      },
      click: (e: Event, table: WebModuleTable, item: unknown) => {
        e.stopPropagation();
        e.preventDefault();

        const rowItem = item as QuoteItem;

        const isShown = table.isExpanded(rowItem);

        if (isShown) table.remExtension(rowItem);
        else {
          table.addExtension(rowItem, this._extendedDetailsTemplateGenerator(rowItem.id));
        }

        return true;
      }
    });

    const tooltipIcons = () =>
      html` <webmodule-tooltip placement="right" class="table-header-tooltip" hoist>
        <div slot="content">
          <div>
            <webmodule-icon library="fa" name="fas-message" color="green"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.notesToClient()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-message" color="black"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.notesToManufacturer()}</span>
          </div>
          <small>${LangFranchiseeQuoteItemsView.tooltipFrameConfiguration()}</small>
          <webmodule-divider
            style="--spacing: 0.25rem; --color: var(--webmodule-color-neutral-300)"
          ></webmodule-divider>
          <div>
            <webmodule-icon library="fa" name="fas-circle-info" color="cyan"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.information()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-file-lines" color="primary"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.supplierNote()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-triangle-exclamation" color="yellow"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.warning()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-circle-exclamation" color="red"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.criticalError()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-wrench" color="red"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.requireValidation()}</span>
          </div>
          <small>${LangFranchiseeQuoteItemsView.tooltipPricing()}</small>
          <webmodule-divider
            style="--spacing: 0.25rem; --color: var(--webmodule-color-neutral-300)"
          ></webmodule-divider>
          <div>
            <webmodule-icon library="fa" name="fas-tags" color="primary"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.dealerPriceAdjustment()}</span>
          </div>
          <div>
            <webmodule-icon library="fa" name="fas-tags"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.supplierPriceAdjustment()}</span>
          </div>
          <div>
            <webmodule-icon library="default" name="no-tax"></webmodule-icon>
            <span>${LangFranchiseeQuoteItemsView.noTax()}</span>
          </div>
        </div>
        <webmodule-icon library="fa" name="far-circle-question"></webmodule-icon>
      </webmodule-tooltip>`;

    addColumnDef('tags', {
      title: () => LangFranchiseeQuoteItemsView.tags(),
      // Suffix content: the legend tooltip sits after the header label.
      headerSuffix: () => tooltipIcons(),
      classes: 'quote-item-tags no-pseudo',
      width: '90px',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        try {
          const container = this.quoteManager.quoteItemContainer(rowItem.id);

          return this.getQuoteItemTags(container);
        } catch {
          return html``;
        }
      }
    });

    addColumnDef('qty', {
      title: () => LangFranchiseeQuoteItemsView.qty(),
      classes: 'quote-item-quantity',
      width: '64px',
      align: 'center',
      fieldName: 'quantity',
      displayValue: (_table: WebModuleTable, item: QuoteItem, _index: number) => {
        const rowItem = item;
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(item.id);

        return hideValue ? html`` : html`${rowItem.quantity}`;
      }
    });
    if (userDataStore.isFabricatorMode) {
      // In fabricator mode, replace the Modified Date column with a Fabrication Status column.
      // Fabrication status data is loaded separately via FabricationService and merged client-side.
      addColumnDef('fabricationStatus', {
        title: () => LangFranchiseeQuoteItemsView.fabricationStatus(),
        classes: 'quote-item-fabrication-status no-pseudo',
        width: '150px',
        align: 'center',
        fieldName: 'fabricationStatus',
        displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
          const rowItem = item as QuoteItem;
          const fabData = this.quoteManager.itemFabricationStatuses.get(rowItem.id);
          if (!fabData || fabData.fabricationStatus === QuoteItemFabricationStatus.None) return html``;
          const itemBadge = ((): TemplateResult => {
            switch (fabData.fabricationStatus) {
              case QuoteItemFabricationStatus.ReadyForFabrication:
                return html`<span
                  class="badge badge-info"
                  style="cursor:pointer"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this.openFabricationJobPicker(rowItem.id);
                  }}
                  ><webmodule-icon
                    library="fa"
                    name=${getQuoteItemFabricationStatusIcon(QuoteItemFabricationStatus.ReadyForFabrication)}
                  ></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.readyForFabrication()}</span
                >`;
              case QuoteItemFabricationStatus.InProduction:
                return html`<span class="badge badge-warning"
                  ><webmodule-icon
                    library="fa"
                    name=${getQuoteItemFabricationStatusIcon(QuoteItemFabricationStatus.InProduction)}
                  ></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.inProduction()}</span
                >`;
              case QuoteItemFabricationStatus.ReadyForDelivery:
                // Opens the delivery checklist for the whole quote, with this item pre-ticked.
                return html`<a
                  href="javascript:void(0)"
                  class="badge badge-success"
                  title=${LangFabricationJobActions.deliveryDialogTitle()}
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void this.recordDeliveryFrom(rowItem.id);
                  }}
                  ><webmodule-icon
                    library="fa"
                    name=${getQuoteItemFabricationStatusIcon(QuoteItemFabricationStatus.ReadyForDelivery)}
                  ></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.readyForDelivery()}</a
                >`;
              case QuoteItemFabricationStatus.Delivered:
                // Opens the recorded delivery read-only - same interaction as the job items table.
                return html`<a
                  href="javascript:void(0)"
                  class="badge badge-secondary"
                  title=${LangFabricationJobActions.deliveryViewHint()}
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void this.viewDeliveryFor(rowItem.id);
                  }}
                  ><webmodule-icon
                    library="fa"
                    name=${getQuoteItemFabricationStatusIcon(QuoteItemFabricationStatus.Delivered)}
                  ></webmodule-icon>
                  ${LangFranchiseeQuoteItemsView.delivered()}</a
                >`;
              default:
                return html``;
            }
          })();

          // The link to the job stays visible for the item's whole life in fabrication, not just
          // while it is merely "assigned" - once a job completes you still want to reach it from
          // the quote.
          const jobLink =
            fabData.fabricationJobId && fabData.fabricationJobNumber
              ? html`<a
                  href="javascript:void(0)"
                  class="badge badge-primary"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    e.preventDefault();
                    goURL('fabricationJob', fabData.fabricationJobId!, 'items');
                  }}
                  >${LangFranchiseeQuoteItemsView.assignedToJob({ jobNumber: String(fabData.fabricationJobNumber) })}</a
                >`
              : html``;

          // Only one status badge, and it is whichever one carries information the other does not.
          // While the item is merely Assigned, its own status says nothing useful and the job's
          // state (Planned / Awaiting Order / Ready for Production) is the answer. From In
          // Production onwards the item's status already reflects what the job did to it, so
          // showing the job state as well would just repeat it - a Completed job beside a
          // "Ready for Delivery" item reads as two statuses for one fact.
          const statusBadge =
            fabData.fabricationStatus === QuoteItemFabricationStatus.AssignedToFabricationJob
              ? fabData.fabricationJobState != null
                ? getFabricationJobStatus(fabData.fabricationJobState, true)
                : html``
              : itemBadge;

          return html`<div class="fabrication-status-cell">${jobLink}${statusBadge}</div>`;
        }
      });
    } else {
      addColumnDef('lastModified', {
        title: () => LangFranchiseeQuoteItemsView.lastModified(),
        classes: 'quote-item-modified no-pseudo',
        width: '124px',
        fieldName: 'lastModifiedDate',
        displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
          const rowItem = item as QuoteItem;

          const dt = new Date(rowItem.lastModifiedDate);
          return html`${dt.toLocaleDateString()} <br />
            ${dt.toLocaleTimeString()}`;
        }
      });
    }

    if (this.quoteCalculationsEnabled()) {
      // Only the column identity (name/caption) is captured here; the per-row value is
      // read live in displayValue below so reconfigured items reflect the freshly-saved
      // quoteCalculations (saveQuoteCalculations swaps the object on each SSI pass).
      for (const qcCol of this.discoverQuoteCalcColumns()) {
        addColumnDef(qcCol.name, {
          title: interpolateExternal(firstValidString(qcCol.caption, qcCol.name)),
          classes: 'quote-calc-col',
          width: '100px',
          align: 'right',
          fieldName: 'xx',
          displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
            const rowItem = item as QuoteItem;

            let colValue = '';
            if (isFrame(rowItem)) {
              // Live read (see note above) — closing over a snapshot here would show stale
              // values until a manual refresh (Bug 229662).
              const liveQc = this.quoteManager.quoteCalcuationData as QCQuoteValues | undefined;
              const qcVal = liveQc?.quoteItemValues
                .find(x => x.quoteItemId === rowItem.id)
                ?.values.find(val => val.name == qcCol.name);

              if (qcVal) colValue = firstValidString(qcVal.displayValue, tryAsNumberString(qcVal.value, 2));
            }
            return html`${colValue}`;
          }
        });
      }
    }

    addColumnDef('extendedListPrice', {
      title: () => LangFranchiseeQuoteItemsView.extListPrice(),
      classes: 'quote-item-price-sup-ext-list supplier-price-col',
      width: '110px',
      align: 'right',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, rowItem: QuoteItem, _index: number) => {
        if (isAnyFreehand(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemExtendedListPrice(rowItem.id);
        return moneyToTemplateResult(value, qCurrency);
      },
      columnAggregate: () =>
        deferrable(() => moneyToTemplateResult(this.quoteManager.getQuoteItemExtendedListPriceTotal(), qCurrency)),
      groupAggregate: items =>
        deferrable(() =>
          moneyToTemplateResult(
            sumGroup(items, id => this.quoteManager.getQuoteItemExtendedListPrice(id)),
            qCurrency
          )
        )
    });
    addColumnDef('multiplier', {
      title: () => LangFranchiseeQuoteItemsView.multiplier(),
      classes: 'quote-item-price-sup-multiplier supplier-price-col',
      width: '76px',
      align: 'right',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, rowItem: QuoteItem, _index: number) => {
        if (isAnyFreehand(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const multiplier = this.quoteManager.getQuoteItemMultiplier(rowItem.id);
        return number4dpToHtml(multiplier);
      }
    });
    // Hide Supplier Price Adjustment column in fabricator mode
    if (!userDataStore.isFabricatorMode) {
      addColumnDef('priceAdjustmentSupplier', {
        title: () => LangFranchiseeQuoteItemsView.supplierPriceAdj(),
        classes: 'quote-item-price-sup-adj supplier-price-col',
        width: '130px',
        align: 'right',
        fieldName: 'xx',
        displayValue: (_table: WebModuleTable, rowItem: QuoteItem, _index: number) => {
          if (isAnyFreehand(rowItem)) return '';
          const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
          if (hideValue) return html``;

          const value = this.quoteManager.getQuoteItemSupplierPriceAdjustment(rowItem.id);
          return moneyToTemplateResult(value, qCurrency);
        },
        columnAggregate: () =>
          deferrable(() =>
            moneyToTemplateResult(this.quoteManager.getQuoteItemSupplierPriceAdjustmentTotal(), qCurrency)
          ),
        groupAggregate: items =>
          deferrable(() =>
            moneyToTemplateResult(
              sumGroup(items, id => this.quoteManager.getQuoteItemSupplierPriceAdjustment(id)),
              qCurrency
            )
          )
      });
    }
    addColumnDef('dealerCost', {
      title: () => LangFranchiseeQuoteItemsView.costPrice(),
      classes: 'quote-item-price-del-cost dealer-price-col',
      width: '110px',
      align: 'right',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemDealerCost(rowItem.id);
        return moneyToTemplateResult(value, qCurrency);
      },
      columnAggregate: () =>
        deferrable(() => moneyToTemplateResult(this.quoteManager.getQuoteItemDealerCostTotal(), qCurrency)),
      groupAggregate: items =>
        deferrable(() =>
          moneyToTemplateResult(
            sumGroup(items, id => this.quoteManager.getQuoteItemDealerCost(id)),
            qCurrency
          )
        )
    });
    addColumnDef('margin', {
      title: `Pricing Metric (%)`,
      classes: 'quote-item-price-del-margin dealer-price-col',
      width: '130px',
      align: 'right',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, rowItem: QuoteItem, _index: number) => {
        if (isDealerQuotePriceAdjustment(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemMarginOrMarkupPercent(rowItem.id);
        return html`${number2dpToHtml(value)}%`;
      }
    });
    addColumnDef('priceAdjustment', {
      title: () => LangFranchiseeQuoteItemsView.dealerPriceAdj(),
      classes: 'quote-item-price-del-adj dealer-price-col',
      width: '120px',
      align: 'right',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, rowItem: QuoteItem, _index: number) => {
        if (isDealerQuotePriceAdjustment(rowItem)) return '';
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const value = this.quoteManager.getQuoteItemDealerPriceAdjustment(rowItem.id);
        return moneyToTemplateResult(value, qCurrency);
      },
      columnAggregate: () =>
        deferrable(() => moneyToTemplateResult(this.quoteManager.getQuoteItemDealerPriceAdjustmentTotal(), qCurrency)),
      groupAggregate: items =>
        deferrable(() =>
          moneyToTemplateResult(
            sumGroup(items, id => this.quoteManager.getQuoteItemDealerPriceAdjustment(id)),
            qCurrency
          )
        )
    });
    addColumnDef('tax', {
      title: () => LangFranchiseeQuoteItemsView.tax(),
      classes: 'quote-item-tax dealer-tax-col',
      width: '110px',
      align: 'right',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, rowItem: QuoteItem) => {
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        const price = this.quoteManager.quoteItemTax(rowItem.id);
        return price === 0 ? '' : moneyToTemplateResult(price, qCurrency);
      },
      columnAggregate: () =>
        deferrable(() => moneyToTemplateResult(this.quoteManager.quotePrice.calculatedTaxAmount, qCurrency)),
      groupAggregate: items =>
        deferrable(() =>
          moneyToTemplateResult(
            sumGroup(items, id => this.quoteManager.quoteItemTax(id)),
            qCurrency
          )
        )
    });

    addColumnDef('price', {
      title: () => LangFranchiseeQuoteItemsView.sellingPrice(),
      classes: 'quote-item-price dealer-price-col',
      width: '120px',
      align: 'right',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;
        const hideValue = this.table!.isExpanded(rowItem) && this.quoteManager.hasBreakdowns(rowItem.id);
        if (hideValue) return html``;

        // Deferred SSI (Task 230006): a batch's composition changed, so supplier-computed (SSI) prices are
        // stale until the user runs Update Price. A badge rather than a bare placeholder - the row is in a
        // money column, so anything that reads like a value invites being taken for one. Carries the same
        // question-mark + tooltip affordance as the group item-count badge, because "Deferred" only means
        // something once you know a batch change caused it and Update Price clears it.
        if (this.quoteManager.ssiDeferred && isSSI(rowItem)) {
          // getToolTip rather than a hand-rolled tooltip: it is the shared affordance (hoisted, with an
          // explicit --max-width) used everywhere else. Opened to the LEFT and width-capped because this
          // is the last column before the pinned actions - a default top/right placement runs straight off
          // the edge of the table and the panel gets clipped.
          return html`<span class="badge badge-warning"
            >${LangFranchiseeQuoteItemsView.pricingUnavailable()}
            ${getToolTip(LangFranchiseeQuoteItemsView.pricingUnavailableTooltip(), {
              placement: 'left',
              maxSize: '16rem'
            })}</span
          >`;
        }

        const price = this.quoteManager.quoteItemPrice(rowItem.id, _table.isExpanded(rowItem) != undefined);
        return moneyToTemplateResult(price, qCurrency);
      },
      columnAggregate: () => deferrable(() => moneyToTemplateResult(this.quoteManager.quoteItemPriceTotal, qCurrency)),
      groupAggregate: items =>
        deferrable(() =>
          moneyToTemplateResult(
            sumGroup(items, id => this.quoteManager.quoteItemPrice(id, false)),
            qCurrency
          )
        ),
      // Second summary row: the overall quote (gross) total, clickable to open the pricing summary drawer.
      // Deferred pricing takes the button away with the figure - the drawer it opens is the same stale
      // arithmetic in more detail, so offering it would just move the wrong number somewhere bigger.
      columnSubAggregate: () =>
        deferrable(
          () =>
            html` <span
              role="button"
              class="quote-item-total-button"
              title=${LangFranchiseeQuoteItemsView.pricingSummary()}
              @click=${() => this.onOpenPricingSummary?.()}
            >
              ${moneyToTemplateResult(this.quoteManager.quotePrice.calculatedGrossTotal, qCurrency)}
            </span>`
        )
    });

    return cols;
  }
}

/**
 * Expander (row-extension) body shown when a quote item row is expanded: the frame
 * option groups, nested sub-frames, price breakdowns and notes.
 *
 * Extracted into its own component so it carries its own encapsulated, theme-aware
 * styles in its shadow root. The host <webmodule-table> renders extension content
 * inside ITS shadow DOM, where the global stylesheet cannot reach - previously the
 * layout was duplicated into the table's customStyles. As a component the styles
 * travel with it, and theming is driven by the inherited --wm-table-* and
 * --webmodule-color-* design tokens, so it tracks the app theme without the
 * hard-coded blue accents the legacy markup used.
 */
@customElement('franchisee-quote-item-expander')
export class FranchiseeQuoteItemExpander extends LitElementLangBase {
  static styles = [
    frameExpanderStyles,
    css`
      /* Breakdown / buy-in rows mirror the parent table columns (inline widths). */
      .simple-table-row-extension {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        width: 100%;
      }
      .simple-table-row-extension .column {
        padding: 0.25rem 1rem;
        font-size: var(--wm-table-cell-font-size, 0.875rem);
        overflow: hidden;
      }

      /* Bootstrap utility classes used inline by the breakdown templates (unavailable
         in the shadow root, so the few that matter are declared here). */
      .ms-1 {
        margin-left: 0.25rem;
      }
      .text-danger {
        color: var(--red, #dc3545);
      }
    `
  ];

  @property({ attribute: false })
  public quoteManager!: FranchiseeQuoteContainerManager;

  @property({ attribute: false })
  public container?: QuoteItemContainer;

  /** Parent table column defs, used to mirror column widths in breakdown rows. */
  @property({ attribute: false })
  public columns: WebModuleTableColumnDef[] = [];

  protected render() {
    if (!this.container) return nothing;
    return this.getExtendedDetails(this.container);
  }

  private getExtendedDetails(quoteItemContainer: QuoteItemContainer, _class?: 'even' | 'odd') {
    if (isFrame(quoteItemContainer.item)) return this.getFrameExtendedDetails(quoteItemContainer, _class);
    else if (isSpecialItem(quoteItemContainer.item))
      return this.getSpecialItemExtendedDetails(quoteItemContainer, _class);

    return this.getOtherLineItemDetails(quoteItemContainer);
  }

  private getNotesTemplate(quoteItemContainer: QuoteItemContainer) {
    if (
      !isEmptyOrSpace(quoteItemContainer.item.comment) ||
      !isEmptyOrSpace(quoteItemContainer.item.commentToManufacturer)
    ) {
      return html` <ul class="list-group small-font">
        ${!isEmptyOrSpace(quoteItemContainer.item.comment)
          ? html` <li class="list-group-item list-group-item-primary">
              <span class="me-3 fw-bold">${LangFranchiseeQuoteItemsView.notesToClient()}:</span>
              ${asMarkdownTemplate(quoteItemContainer.item.comment)}
            </li>`
          : nothing}
        ${!isEmptyOrSpace(quoteItemContainer.item.commentToManufacturer)
          ? html` <li class="list-group-item list-group-item-primary">
              <span class="me-3 fw-bold">${LangFranchiseeQuoteItemsView.notesToManufacturer()}:</span>
              ${asMarkdownTemplate(quoteItemContainer.item.commentToManufacturer)}
            </li>`
          : nothing}
      </ul>`;
    }
    return html``;
  }

  private getBuyInValidation(quoteItemContainer: QuoteItemContainer) {
    return this.quoteManager.getPriceValidation(quoteItemContainer.item.id);
  }

  /**
   * Inline width for an expander breakdown cell so it lines up with the parent
   * column. The new table sizes columns from the `width`/`minWidth` on the column
   * def (no global colpx classes), so the breakdown rows mirror that here.
   */
  private breakdownCellStyle(col: WebModuleTableColumnDef): string {
    if (col.width) return `flex:0 0 ${col.width};width:${col.width}`;
    return `flex:1 1 0;${col.minWidth ? `min-width:${col.minWidth};` : ''}`;
  }

  private getSpecialItemExtendedDetails(quoteItemContainer: QuoteItemContainer, _classMap?: 'even' | 'odd') {
    const warningTemplate = (): TemplateResult => {
      if (!isEmptyOrSpace(quoteItemContainer.item.description) || isSupplierSpecialItem(quoteItemContainer.item))
        return html``;

      return html`
        <ul class="list-group">
          <li class="list-group-item list-group-item-warning">
            <span class="me-3 fw-bold">${LangFranchiseeQuoteItemsView.supplierReference()}</span>
            ${LangFranchiseeQuoteItemsView.supplierMustProvideReference()}
          </li>
        </ul>
      `;
    };

    return isSupplierSpecialItem(quoteItemContainer.item)
      ? this.getFrameExtendedDetails(quoteItemContainer, _classMap)
      : html` <div class="extended-options-line main-body">
          ${warningTemplate()} ${this.getNotesTemplate(quoteItemContainer)}
        </div>`;
  }

  private getOtherLineItemDetails(quoteItemContainer: QuoteItemContainer) {
    const hasBreakdowns = quoteItemContainer.price.breakdowns && quoteItemContainer.price.breakdowns.length > 0;
    const hasCommentOnShippingOrFreehand =
      (isShipping(quoteItemContainer.item) ||
        isDefaultShipping(quoteItemContainer.item) ||
        isAnyFreehand(quoteItemContainer.item) ||
        isSSI(quoteItemContainer.item)) &&
      !isEmptyOrSpace(quoteItemContainer.item.comment);

    // Show template if item has price breakdowns OR is shipping/freehand with a comment
    if (!hasBreakdowns && !hasCommentOnShippingOrFreehand) return html``;
    const getBreakDownRow = (breakDown: QuoteItemPriceBreakdown) => {
      const titleDisplay = (description: string | undefined) => {
        return html`<span class="ms-1"
          ><webmodule-icon library="fa" name="fas-play"></webmodule-icon
          ><span class="ms-1"> ${description ?? ''}</span></span
        >`;
      };
      const buyInValueTemplate = breakDown?.calculatedGrossSellingPrice
        ? moneyToTemplateResult(breakDown?.calculatedGrossSellingPrice)
        : '';

      const addColumn = () => {
        return this.columns.map(x => {
          if (!x.visible) {
            return html``;
          }

          const classes = x.classes;
          let value;
          switch (x.id) {
            case 'thumbnail':
              value = '';
              break;
            case 'location':
              value = titleDisplay(breakDown.name);
              break;
            case 'options':
              value = '';
              break;
            case 'qty':
              value = quoteItemContainer.item.quantity;
              break;
            case 'price':
              value = buyInValueTemplate;
              break;
            case 'extendedListPrice': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemExtendedListPrice(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'multiplier': // TODO: review
              value = number4dpToHtml(this.quoteManager.getQuoteItemMultiplier(quoteItemContainer.item.id, breakDown));
              break;
            case 'priceAdjustmentSupplier': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemSupplierPriceAdjustment(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'dealerCost': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemDealerCost(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'margin': // TODO: review
              value = html`${number2dpToHtml(
                this.quoteManager.getQuoteItemMarginOrMarkupPercent(quoteItemContainer.item.id)
              )}%`;
              break;
            case 'priceAdjustment': // TODO: review
              value = moneyToTemplateResult(
                this.quoteManager.getQuoteItemDealerPriceAdjustment(quoteItemContainer.item.id, breakDown)
              );
              break;
            case 'tax': // TODO: review
              {
                const price = this.quoteManager.quoteItemTax(quoteItemContainer.item.id, breakDown);
                value = price === 0 ? '' : moneyToTemplateResult(price);
              }
              break;
          }

          return html` <div class="column ${classes}" style=${this.breakdownCellStyle(x)}>${value}</div> `;
        });
      };

      return html` <div class="extended-options-line">
        <div class="tr simple-table-row simple-table-row-extension">
          <div class="column" style="flex:0 0 2rem;width:2rem"></div>
          ${addColumn()}
        </div>
      </div>`;
    };

    const breakDownTemplate =
      quoteItemContainer.price.breakdowns && quoteItemContainer.price.breakdowns.length > 0
        ? html` ${quoteItemContainer.price.breakdowns.map(breakdown => getBreakDownRow(breakdown))} `
        : nothing;

    return html` <div class="extended-options-line main-body">
      ${breakDownTemplate}${this.getNotesTemplate(quoteItemContainer)}
    </div>`;
  }

  private getFrameExtendedDetails(quoteItemContainer: QuoteItemContainer, _classMap?: 'even' | 'odd'): TemplateResult {
    //only here if we are a frame and also a v6 frame
    if (!isV6(quoteItemContainer)) return html``;
    if (!isValidV6ConfigVersion()) {
      const message = LangFranchiseeQuoteItemsView.supplierIsOffline();
      return html`${message}`;
    }

    const data = getV6FrameForContainer(quoteItemContainer);

    const validationsTemplate = (
      validationType: FrameValidationType,
      cssClass: string
    ): TemplateResult[] | TemplateResult => {
      const stuff = data?.validations?.filter(x => x.type === validationType) ?? [];
      if (stuff.length === 0) return html``;
      const rows = stuff.map(issue => {
        return html` <li class="list-group-item ${cssClass}">
          <span class="me-3 fw-bold">${interpolateExternal(issue.title)}</span>${interpolateExternal(issue.text ?? '')}
        </li>`;
      });
      return html`
        <ul class="list-group">
          ${rows}
        </ul>
      `;
    };

    const criticalTemplate = (reason: string): TemplateResult[] | TemplateResult => {
      const cssClass = 'list-group-item-danger';
      const stuff = [reason];
      if (stuff.length === 0) return html``;
      const rows = stuff.map(issue => {
        return html` <li class="list-group-item ${cssClass}">${interpolateExternal(issue ?? '')}</li>`;
      });
      return html`
        <ul class="list-group">
          ${rows}
        </ul>
      `;
    };

    const frameDetailsSection = isFrame(quoteItemContainer.item)
      ? data
        ? getFrameDetailsTemplate(data, true)
        : criticalTemplate(LangFranchiseeQuoteItemsView.invalidFrameData())
      : undefined;
    // Why the item needs revalidating, with both versions, when its customer code's version moved (SUPVER-008).
    const customerVersions = this.quoteManager.currentStateRequiresValidation
      ? getQuoteFrameCustomerVersions(quoteItemContainer)
      : undefined;
    const customerVersionTemplate = customerVersions
      ? html`<ul class="list-group">
          <li class="list-group-item list-group-item-warning">
            ${LangFranchiseeQuoteManager.itemOutOfDateCustomerVersion(customerVersions)}
          </li>
        </ul>`
      : html``;
    const validationTemplates = html` <div class="extended-options-line small-font main-body">
      ${frameDetailsSection} ${customerVersionTemplate}
      ${validationsTemplate(FrameValidationType.critical, 'list-group-item-danger')}
      ${validationsTemplate(FrameValidationType.warning, 'list-group-item-warning')}
      ${validationsTemplate(FrameValidationType.information, 'list-group-item-info')}
      ${validationsTemplate(FrameValidationType.note, 'list-group-item-note')}
      ${this.getNotesTemplate(quoteItemContainer)}
    </div>`;

    const getBuyInRow = (buyInItem: FrameBuyInItem, heightFirst: null | boolean) => {
      const buyIn = this.quoteManager.lookupBuyInData(
        quoteItemContainer,
        buyInItem.code,
        buyInItem.extraDetails?.['SuppCode'] ?? '',
        `Length=${buyInItem.extraDetails?.['Length'] ?? ''};Width=${buyInItem.extraDetails?.['Width'] ?? ''};Height=${buyInItem.extraDetails?.['Height'] ?? ''}`
      );

      const codeDisplay = () => {
        return html`(${buyInItem.extraDetails?.['SuppCode'] ?? ''} - ${buyInItem.code})`;
      };

      const priceValidations = () => {
        const validations = this.getBuyInValidation(quoteItemContainer);

        if (validations?.details && validations.details.length > 0)
          return validations.details.map(x => html`<br /><span class="text-danger">${x.message}</span>`);

        return html``;
      };

      const titleDisplay = (description: string | undefined) => {
        return html`${description ?? ''} <br />
          ${codeDisplay()} ${priceValidations()}`;
      };
      const buyInValueTemplate = buyIn?.calculatedGross ? moneyToTemplateResult(buyIn.calculatedGross) : '';

      const addBuyInColumn = () => {
        return this.columns.map(x => {
          if (!x.visible) {
            return html``;
          }

          const classes = x.classes;
          const value =
            x.id === 'thumbnail'
              ? supplierItemContentTypeDisplay(buyInItem.resourceType, false)
              : x.id === 'location'
                ? titleDisplay(buyIn?.description)
                : x.id === 'options'
                  ? getBuyInDetailsTemplate(buyInItem, heightFirst)
                  : x.id === 'qty'
                    ? buyInItem.quantity
                    : x.id === 'price'
                      ? buyInValueTemplate
                      : html``;

          return html` <div class="column ${classes}" style=${this.breakdownCellStyle(x)}>${value}</div> `;
        });
      };

      return html` <div class="extended-options-line ">
        <div class="tr simple-table-row simple-table-row-extension">
          <div class="column" style="flex:0 0 2rem;width:2rem"></div>
          ${addBuyInColumn()}
        </div>
      </div>`;
    };

    const heightFirst = this.quoteManager.displayHeightFirst;
    return html`${data?.buyIn?.map((item, _index) => getBuyInRow(item, heightFirst))} ${validationTemplates}`;
  }
}

@customElement('franchisee-quote-item-selector-table')
export class FranchiseeQuoteItemSelectorTable extends LitElementLangBase {
  setSelectedItems(_selectedKeys: string[]) {
    if (this.table) this.table.selectedKeys = _selectedKeys;
  }
  quoteApi: QuoteApi = getApiFactory().quote();
  @query('#quote-items-selector-table')
  table?: WebModuleLitTable;
  /** If true the table is readonly and selection/filter UI is limited */
  @property({ type: Boolean })
  public readonly: boolean = false;

  @property({ type: Boolean })
  public allowOpenLink: boolean = false;
  @property({ type: Array })
  private _quoteItemKeys: string[] = [];
  public get quoteItemKeys(): string[] {
    return this._quoteItemKeys;
  }
  public set quoteItemKeys(value: string[]) {
    this._quoteItemKeys = value;
    this.setSelectedItems(value);
  }
  /** When true, show the filter controls above the table (unless readonly) */
  @property({ type: Boolean })
  public showFilter: boolean = true;
  @property({
    type: Array,
    hasChanged(newVal: object, oldVal: object) {
      return newVal !== oldVal;
    }
  })
  public columnConfiguration: TableColumnConfiguration[] = [];
  @property({ type: Object, attribute: false })
  public quoteManager!: FranchiseeQuoteContainerManager;

  @consume({ context: imagePreviewContext })
  @property({ attribute: false })
  public imagePreview?: ImagePreview;

  @state()
  private _columns: WebModuleLitTableColumnDef[] = this.getColumns();

  /** live items (active + excluded-from-pricing) in grouped display order, special items removed */
  @state()
  private _rows: QuoteItem[] = [];
  // Item id → display number. Per-container numbering (Task 230006): each container numbers its own members
  // from 1 - the trunk has one counter and every batch restarts - so a quote with no batches is just flat
  // 1..N and the group header is what tells a loose "1" from a batch's "1". EVERY line is numbered, the
  // charge lines (shipping / SSI / adjustment) included: a charge is a member of its container pinned to the
  // bottom of it, so a batch of two windows plus its shipping numbers 1, 2, 3 and its header counts 3.
  // Sourced from QuoteContainerManager.itemDisplayNumbers, which the reports and the purchase order mirror.
  private _numberByItemId = new Map<string, string>();
  /**
   * row item id -> group headers injected above that row via before-row-render. Keyed by item id
   * (not numeric index) so it stays correct when the table re-indexes rows - e.g. visibleMode
   * 'selected' / 'show selected only', which filters and re-bases the row indices.
   */
  private _headersBeforeItemId = new Map<string, QuoteItemGroupHeader[]>();

  getSelectedItems(): string[] {
    if (this.table) {
      return this.table.selectedKeys as string[];
    }
    return [];
  }
  protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
    super.firstUpdated(_changedProperties);
    await this.refreshData();
    this.dispatchEvent(new CustomEvent('wm-component-ready', { bubbles: true, composed: true, detail: this }));
  }

  /**
   * Build the selectable rows from the same canonical ordering the main quote items table uses:
   * live items (active + excluded-from-pricing) in grouped display order, with group headers. The
   * item number is taken from the position in the full ordered list - including special items,
   * which are then filtered out of the selectable rows - so the number matches the quote view.
   * Group headers are re-placed against the filtered rows: a header attaches above the first
   * linkable member of its group; groups with no linkable members are dropped (the selector only
   * shows headers that have selectable rows beneath them).
   */
  public async refreshData(): Promise<void> {
    await this.quoteManager.needsQuoteItems(false);
    await this.quoteManager.performItemSorting();
    await this.quoteManager.needsItemGrouping();

    const built = buildQuoteItemRows(this.quoteManager, false);
    // Canonical per-section numbering (keyed by commonId), including the charge lines that are forced to
    // the end of their section — see QuoteContainerManager.itemDisplayNumbers.
    const numbers = this.quoteManager.itemDisplayNumbers();
    this._numberByItemId = new Map(built.rows.map(item => [item.id, numbers.get(item.commonId) ?? '']));

    const isLinkable = (item: QuoteItem) => !isSpecialItem(item) && !isSupplierSpecialItem(item);
    const linkableCommonIds = new Set(built.rows.filter(isLinkable).map(x => x.commonId));
    const hasLinkableMember = (header: QuoteItemGroupHeader) =>
      header.itemCommonIds.some(id => linkableCommonIds.has(id));

    const rows: QuoteItem[] = [];
    const headersBeforeItemId = new Map<string, QuoteItemGroupHeader[]>();
    let pendingHeaders: QuoteItemGroupHeader[] = [];
    built.rows.forEach((item, fullIndex) => {
      const headers = built.headersBeforeIndex.get(fullIndex)?.filter(hasLinkableMember);
      if (headers && headers.length > 0) pendingHeaders.push(...headers);
      if (!isLinkable(item)) return;
      if (pendingHeaders.length > 0) {
        // anchor the headers to the row item itself, so placement survives row re-indexing/filtering
        headersBeforeItemId.set(item.id, pendingHeaders);
        pendingHeaders = [];
      }
      rows.push(item);
    });

    this._rows = rows;
    this._headersBeforeItemId = headersBeforeItemId;
    this.requestUpdate();
  }
  dispatchCustom(values: object) {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };

    this.dispatchEvent(new CustomEvent(`wm-quote-item-action`, options));
  }

  getOptionsOneLiner(quoteItemContainer: QuoteItemContainer) {
    //We should only progress if item is a configuration from supplier.
    if (!isV6(quoteItemContainer)) return '';
    //We are only here if we are a v6 Frame
    const data = getV6QuoteItemForContainer(quoteItemContainer);
    return getV6QuoteItemSubtitleOrFrameDimensions(data);
  }

  @property({ type: Boolean })
  public showSelectedOnly = false;

  get filteredItems(): QuoteItem[] {
    return this._rows;
  }
  protected render() {
    const keyEvent = (item: QuoteItem) => {
      return item.id;
    };

    const rowIdEvent = (item: QuoteItem) => {
      return item.id;
    };

    return html` <webmodule-lit-table
      visibleMode=${this.readonly || this.showSelectedOnly ? 'selected' : 'all'}
      id="quote-items-selector-table"
      class="quote-items-table"
      .rowClass=${'tr'}
      .selectmode=${this.readonly ? 'none' : 'multi'}
      .colClass=${'column'}
      .keyevent=${keyEvent}
      .rowidevent=${rowIdEvent}
      tablestyle="nestedtable"
      .columns=${this._columns}
      pageLength="100"
      .data=${this.filteredItems ?? []}
      .clickrows=${true}
      @before-row-render=${this._beforeRowRenderEvent}
    >
    </webmodule-lit-table>`;
  }

  /**
   * group headers are injected above their first member row through the table's before-row-render
   * hook, mirroring the main quote items table. They are read-only here (no drag/drop or edit
   * menu) - the selector only displays the grouping for context. Looked up by item id so headers
   * stay correctly placed even when the table filters/re-indexes rows (e.g. "show selected only").
   */
  private _beforeRowRenderEvent = (e: CustomEvent<{ item: QuoteItem; index: number; template?: TemplateResult }>) => {
    const headers = this._headersBeforeItemId.get(e.detail.item.id);
    if (headers) e.detail.template = html`${headers.map(header => this.groupHeaderTemplate(header))}`;
  };

  private groupHeaderTemplate(header: QuoteItemGroupHeader): TemplateResult {
    const excludedBadge = header.attributes.excludeFromPricing
      ? html`<span class="badge quote-item-group-badge">${LangFranchiseeQuoteItemsView.excludedFromPricing()}</span>`
      : nothing;
    return html`<div class="quote-item-group-header">
      <span class="quote-item-group-title">
        <webmodule-icon library="fa" name="fas-layer-group"></webmodule-icon>
        <span class="quote-item-group-name">${header.name}</span>
        <webmodule-tooltip content=${LangFranchiseeQuoteItemsView.groupItemCountSelectorTooltip()} hoist>
          <span class="badge quote-item-group-badge"
            >${LangFranchiseeQuoteItemsView.groupItemCount({ count: header.itemCommonIds.length })}
            <webmodule-icon library="fa" name="far-circle-question"></webmodule-icon
          ></span>
        </webmodule-tooltip>
        ${excludedBadge}
      </span>
    </div>`;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
  private async openItem(item: QuoteItem) {
    this.dispatchCustom({
      item: item,
      quoteAction: quoteItemAction.edit
    });
  }

  private getColumns(): WebModuleLitTableColumnDef[] {
    const cols: WebModuleLitTableColumnDef[] = [];

    const addColumnDef = (columnId: string, column: WebModuleLitTableColumnDef) => {
      cols.push({ ...column, id: columnId, sortable: false });
    };

    addColumnDef('number', {
      title: () => LangFranchiseeQuoteItemsView.itemNumber(),
      classes: 'colpxmax-64 quote-item-no',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;
        const itemNumber = this._numberByItemId.get(rowItem.id) ?? '';

        if (rowItem.isRestrictedToPowerUser) return html`${itemNumber} <span class="power-user"></span>`;
        return html`${itemNumber}`;
      }
    });
    addColumnDef('thumbnail', {
      title: () => LangFranchiseeQuoteItemsView.image(),
      classes: 'colpxmax-64 quote-item-image',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const row = item as QuoteItem;

        const src =
          row.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(row)
            : this.quoteApi.convertQuoteItemAssetPathToUrl(
                row.quoteId,
                row.id,
                row.virtualThumbnailPath,
                thumbnailTag(this.quoteManager, row)
              );

        const imagePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();

          if (isFrame(row) || !isEmptyOrSpace(row.virtualThumbnailPath))
            this.imagePreview?.showImagePreview(
              this.quoteApi.convertQuoteItemAssetPathToUrl(
                row.quoteId,
                row.id,
                row.virtualThumbnailPath,
                thumbnailTag(this.quoteManager, row)
              ),
              'quote-item-thumb'
            );
        };

        const hidePreview = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.imagePreview?.hideImagePreview();
        };

        return html` <div
          @mouseenter=${imagePreview}
          @mouseleave=${hidePreview}
          @touchstart=${imagePreview}
          @touchend=${hidePreview}
          @touchcancel=${hidePreview}
        >
          <img
            class="quote-item-thumbnail"
            alt="Thumbnail for quote item"
            src="${src}"
            style="width:48px; height:48px"
          />
        </div>`;
      }
    });
    addColumnDef('location', {
      title: () => LangFranchiseeQuoteItemsView.frameTitleAndDescription(),
      classes: 'colpx-240 quote-item-title-selector',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        const description = html`<span class="ms-2">${' - ' + rowItem.description}</span>`;
        const title = isEmptyOrSpace(rowItem.title) ? LangFranchiseeQuoteItemsView.frameTitle() : rowItem.title;

        if (this.allowOpenLink) {
          const clickEvent = async (e: Event) => {
            e.stopPropagation();
            e.preventDefault();

            await this.openItem(rowItem);
          };

          const title = isEmptyOrSpace(rowItem.title) ? LangFranchiseeQuoteItemsView.frameTitle() : rowItem.title;
          return html`<a class="quote-item-link" href="#" @click="${clickEvent}"
            ><span>${firstValidString(title, LangFranchiseeQuoteItemsView.untitled())}</span> ${description}</a
          > `;
        }

        return html`<span>${firstValidString(title, LangFranchiseeQuoteItemsView.untitled())}</span> ${description}`;
      }
    });

    addColumnDef('options', {
      title: html`<span>${LangFranchiseeQuoteItemsView.options()}</span>`,
      classes: 'colpx-160 quote-item-description',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown, _index: number) => {
        const rowItem = item as QuoteItem;

        try {
          const container = this.quoteManager.quoteItemContainer(rowItem.id);

          return html`<span>${this.getOptionsOneLiner(container)}</span>`;
        } catch {
          return html``;
        }
      }
    });

    return cols;
  }
}

/**
 * Modal dialog for selecting quote items using FranchiseeQuoteItemSelectorTable
 */
@customElement('quote-item-selector-modal')
export class QuoteItemSelectorModal extends ModalDialog {
  private _quoteManager: FranchiseeQuoteContainerManager;
  private _selectedItem: QuoteItem | null = null;

  // Use @query to directly access the table in the template
  @query('franchisee-quote-item-selector-table')
  private _selectorTable!: FranchiseeQuoteItemSelectorTable;
  private _selectedKeys: string[];

  constructor(quoteManager: FranchiseeQuoteContainerManager, selectedKeys?: string[]) {
    super();
    this._selectedKeys = selectedKeys || [];
    this._quoteManager = quoteManager;

    this._modalSize = 'modal-xl';
  }

  /**
   * Gets the selected quote item, if any
   */
  get selectedItem(): QuoteItem | null {
    return this._selectedItem;
  }

  /**
   * @override
   * Initialize the table after first render
   */
  protected async firstUpdated(changedProperties: PropertyValues): Promise<void> {
    await super.firstUpdated(changedProperties);
    this._selectorTable.setSelectedItems(this._selectedKeys);
    // Set the quoteManager on the table component
    this._selectorTable.quoteManager = this._quoteManager;
  }

  /**
   * @override
   */
  protected getTitle(): Snippet {
    return LangFranchiseeQuoteItemsView.linkQuoteItemsToSupport();
  }
  @state()
  _showSelectedOnly = false;

  @state()
  showFilter = true;
  @query('#table')
  table?: FranchiseeQuoteItemSelectorTable;
  @query('#showSelectedOnlyToggle')
  showSelectedOnlyToggle?: WebmoduleToggle;
  /**
   * @override
   */
  protected bodyTemplate(): EventTemplate {
    const filterTemplate = () => {
      if (this.readonly) return html``;
      if (!this.showFilter) return html``;
      return html` <div class="quote-item-filter mb-2">
        <webmodule-toggle
          id="showSelectedOnlyToggle"
          ?checked=${this._showSelectedOnly}
          @webmodule-change=${(e: Event) => {
            // webmodule-toggle emits webmodule-change; read checked from the element
            const tgt = e.currentTarget as any;
            this._showSelectedOnly = !!tgt?.checked;
          }}
        >
          ${LangFranchiseeQuoteItemsView.showOnlySelectedItems()}
        </webmodule-toggle>
      </div>`;
    };
    return html`
      <div class="quote-item-selector-container">
        ${filterTemplate()}
        <franchisee-quote-item-selector-table
          id="table"
          .quoteManager=${this._quoteManager}
          .showSelectedOnly=${this._showSelectedOnly}
        ></franchisee-quote-item-selector-table>
      </div>
    `;
  }

  /**
   * @override
   */
  protected footerTemplate(): TemplateResult {
    return this.createConfirmCancelButtons(
      LangFranchiseeQuoteItemsView.linkQuoteItems(),
      async () => {
        const selectedKeys = this._selectorTable.getSelectedItems();
        this.result = { success: true, keys: selectedKeys };
        this.hideModal();
      },
      buttonsSaveCancel().cancel
    );
  }
  result: { success: boolean; keys: string[] } = { success: false, keys: [] };
  get modalClasses(): string {
    return super.modalClasses + ' fixed-height';
  }
  public name(): string {
    return 'quote-item-selector-modal';
  }
}
